import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  CAPABILITIES,
  ROLE_LABELS,
  ROLE_SUMMARY,
  STAFF_ROLES,
  can,
  type StaffRole,
} from "@/lib/staff-roles";
import {
  listStaffAccounts,
  setStaffRole,
  type StaffAccount,
} from "@/lib/staff-roles.functions";

export const Route = createFileRoute("/_authenticated/admin/team")({
  head: () => ({
    meta: [
      { title: "Roles & Access | Coffman Cove Lodging Staff" },
      {
        name: "description",
        content:
          "Who on the Coffman Cove Lodging crew can approve requests, assign rooms and see the money.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TeamPage,
});

function TeamPage() {
  const { staffRole } = Route.useRouteContext();
  const canManage = can(staffRole, "manageRoles");

  const [accounts, setAccounts] = useState<StaffAccount[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canManage) return;
    let cancelled = false;
    void listStaffAccounts()
      .then((res) => {
        if (!cancelled) setAccounts(res.accounts);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load the sign-in list.");
      });
    return () => {
      cancelled = true;
    };
  }, [canManage]);

  async function changeRole(userId: string, role: StaffRole | null) {
    setBusy(userId);
    setError(null);
    try {
      await setStaffRole({ data: { userId, role } });
      const res = await listStaffAccounts();
      setAccounts(res.accounts);
    } catch {
      setError("That change did not save. You cannot change your own role.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <h1 className="text-3xl font-semibold">Roles &amp; Access</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Only approved people can approve booking requests and assign rooms and boats. Here is
        exactly what each role can do on these pages.
      </p>

      <section className="mt-8 overflow-x-auto rounded-2xl border border-border">
        <table className="w-full min-w-[42rem] text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="px-4 py-3 font-semibold">Can do this</th>
              {STAFF_ROLES.map((role) => (
                <th key={role} className="px-4 py-3 text-center font-semibold">
                  {ROLE_LABELS[role]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CAPABILITIES.map((cap) => (
              <tr key={cap.key} className="border-t border-border">
                <td className="px-4 py-3">{cap.label}</td>
                {STAFF_ROLES.map((role) => (
                  <td key={role} className="px-4 py-3 text-center">
                    {cap.roles.includes(role) ? (
                      <span className="text-[var(--color-ember)]" aria-label="Yes">
                        ●
                      </span>
                    ) : (
                      <span className="text-muted-foreground/40" aria-label="No">
                        –
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mt-6 grid gap-3 sm:grid-cols-2">
        {STAFF_ROLES.map((role) => (
          <div key={role} className="rounded-xl border border-border p-4">
            <h2 className="font-semibold">{ROLE_LABELS[role]}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{ROLE_SUMMARY[role]}</p>
          </div>
        ))}
      </section>

      {canManage ? (
        <section className="mt-10">
          <h2 className="text-xl font-semibold">Who has access</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Pick a role for each person. &ldquo;No access&rdquo; removes them from the staff pages
            entirely. You cannot change your own role.
          </p>
          {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}
          <div className="mt-4 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[36rem] text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Last signed in</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                </tr>
              </thead>
              <tbody>
                {accounts === null ? (
                  <tr>
                    <td className="px-4 py-4 text-muted-foreground" colSpan={3}>
                      Loading…
                    </td>
                  </tr>
                ) : (
                  accounts.map((account) => (
                    <tr key={account.userId} className="border-t border-border">
                      <td className="px-4 py-3">{account.email}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {account.lastSignInAt
                          ? new Date(account.lastSignInAt).toLocaleDateString()
                          : "Never"}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          className="rounded-lg border border-border bg-background px-3 py-2"
                          value={account.role ?? ""}
                          disabled={busy === account.userId}
                          aria-label={`Role for ${account.email}`}
                          onChange={(event) =>
                            void changeRole(
                              account.userId,
                              event.target.value === "" ? null : (event.target.value as StaffRole),
                            )
                          }
                        >
                          <option value="">No access</option>
                          {STAFF_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {ROLE_LABELS[role]}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <p className="mt-8 rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          Only an admin can change who has access. Ask an admin if you need more than the
          {" "}
          {ROLE_LABELS[staffRole]} role.
        </p>
      )}
    </main>
  );
}
